import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { LoadPanel } from 'devextreme-react/load-panel';
import FilterBuilder, { CustomOperation as FilterBuilderCustomOperation, type FilterBuilderTypes } from 'devextreme-react/filter-builder';

import { Field } from './IFilterSelectionForm';
import GroupFilterDto from '../../classes/dtos/datasourcemodule/GroupFilterDto';
import DataTableFieldParameterDefinitionDto from '../../classes/dtos/datasourcemodule/DataTableFieldParameterDefinitionDto';
import { FilterExpression, deriveFilterExpression, filterExpressionToGroupFilter, collectOperationsByField } from './FilterConverter';
import { buildParameterOperations, buildFilteringFields, CONSTANT_OPERATION_DESCRIPTIONS, loadFilteringCapabilities, collectParameterOptions, FilterParameterOption } from './FilterFieldsShared';
import FilteringCapabilitiesDto from '../../classes/dtos/FilteringCapabilitiesDto';

import './FilterBuilderInline.scss';

export type FilterBuilderParameter = FilterParameterOption;

// Variable déclarée dans une grille de mapping (colonne FILTER, mode complexe) : nom + champ porteur.
// Sert à alimenter la picklist de variables de l'onglet Filters avant même qu'elles ne soient câblées
// dans l'arbre (voir DeclaredParameters ci-dessous).
export interface DeclaredParameter {
    TechnicalName: string;
    FieldReferenceId: number;
}

// Agrège les variables déclarées par ligne (mapping.DeclaredVariables, alimenté par le popup FILTER en
// mode complexe — indépendant des conditions du mode simple portées par mapping.Filters), dédupliquées
// par TechnicalName (première occurrence gagnante). Le FieldReferenceId retenu est celui de la ligne
// porteuse (FieldReferenceId, ou SourceFieldReferenceId pour Synchro) : il ne sert qu'à résoudre le
// FieldType de la variable côté FilterBuilder.
export function collectDeclaredParameters(
    mappings: ReadonlyArray<{ FieldReferenceId?: number; SourceFieldReferenceId?: number; DeclaredVariables?: DataTableFieldParameterDefinitionDto[] | null }>
): DeclaredParameter[] {
    const byTechnicalName = new Map<string, DeclaredParameter>();
    mappings.forEach((mapping) => {
        const fieldReferenceId = mapping.FieldReferenceId ?? mapping.SourceFieldReferenceId ?? 0;
        (mapping.DeclaredVariables ?? []).forEach((definition) => {
            const technicalName = definition.TechnicalName.trim();
            if (technicalName.length > 0 && !byTechnicalName.has(technicalName)) {
                byTechnicalName.set(technicalName, { TechnicalName: technicalName, FieldReferenceId: fieldReferenceId });
            }
        });
    });
    return Array.from(byTechnicalName.values());
}

// FieldReferenceIds des lignes de mapping cochées (IsSelected). SourceFieldReferenceId pour Synchro,
// FieldReferenceId sinon. Une ligne sans IsSelected explicite (Synchro n'a pas ce concept) compte comme
// cochée. Sert à restreindre les champs proposés par le FilterBuilder (voir SelectedFieldReferenceIds).
export function collectSelectedFieldReferenceIds(
    mappings: ReadonlyArray<{ FieldReferenceId?: number; SourceFieldReferenceId?: number; IsSelected?: boolean }>
): number[] {
    const ids = new Set<number>();
    mappings.forEach((mapping) => {
        if (mapping.IsSelected === false) {
            return;
        }
        const fieldReferenceId = mapping.FieldReferenceId ?? mapping.SourceFieldReferenceId ?? 0;
        if (fieldReferenceId > 0) {
            ids.add(fieldReferenceId);
        }
    });
    return Array.from(ids);
}

interface FilterBuilderInlineProps {
    TableReferenceId: number;
    Filter: GroupFilterDto | null;
    SetFilter: (filter: GroupFilterDto | null) => void;
    // Variables déjà nommées dans les grilles de mapping : sélectionnables ici en plus de celles déjà
    // présentes dans l'arbre. Figées au (re)chargement, comme le filtre.
    DeclaredParameters?: DeclaredParameter[];
    // FieldReferenceIds des lignes de mapping cochées : le FilterBuilder ne propose que ces champs (parmi
    // les champs filtrables de la source), plus ceux déjà référencés par le filtre chargé (sinon E1048).
    // undefined = aucune restriction. Figé au (re)chargement, comme le filtre.
    SelectedFieldReferenceIds?: number[];
}

// FilterBuilder unique (pas de Popup) : chaque champ ne figure qu'une fois, avec les opérateurs natifs
// (Constante) et paramétrés (Variable, suffixe "v") réunis dans le même menu opérateur — une ligne de
// séparation CSS (voir FilterBuilderInline.scss) distingue visuellement les deux blocs.
// Chaque modification est immédiatement remontée via SetFilter.
// Fusionne les variables de l'arbre et les variables déclarées dans les grilles, dédupliquées par
// TechnicalName — l'arbre gagne (il porte l'Id de définition réel).
function mergeParameterOptions(treeOptions: FilterParameterOption[], declaredOptions: FilterParameterOption[]): FilterParameterOption[] {
    const byTechnicalName = new Map<string, FilterParameterOption>();
    declaredOptions.forEach((option) => byTechnicalName.set(option.TechnicalName, option));
    treeOptions.forEach((option) => byTechnicalName.set(option.TechnicalName, option));
    return Array.from(byTechnicalName.values());
}

// Résolveur neutre pour la canonicalisation : on ne compare que la structure (champ / opérateur /
// valeur), pas le typage — les deux côtés de la comparaison coercent donc les littéraux de la même façon.
const CANONICAL_RESOLVE_TYPE = (_fieldReferenceId: number): number => 0;

// Forme canonique (sans Id) d'une expression FilterBuilder : round-trip par le DTO puis re-dérivation.
// Deux expressions sémantiquement égales produisent la même chaîne, quelle que soit la façon dont
// FilterBuilder a (re)normalisé l'arbre en interne (imbrication, customOperations...).
// `resolveType` réel à l'aller (les constantes date sont normalisées "yyyy-MM-dd" locale par
// FilterConstantValue quel que soit le format d'entrée), résolveur neutre au retour (comparaison
// purement structurelle du scalaire JSON décodé).
function canonicalizeExpression(
    expression: FilterExpression | null | undefined,
    resolveType: (fieldReferenceId: number) => number
): string {
    return JSON.stringify(deriveFilterExpression(filterExpressionToGroupFilter(expression, resolveType), CANONICAL_RESOLVE_TYPE));
}

const FilterBuilderInline: React.FC<FilterBuilderInlineProps> = ({ TableReferenceId, Filter, SetFilter, DeclaredParameters, SelectedFieldReferenceIds }) => {

    const [loading, setLoading] = useState(true);

    const [filterValue, setFilterValue] = useState<FilterExpression>([]);

    // Capacités brutes (toutes les colonnes filtrables de la source), rechargées seulement au changement
    // de table. La restriction aux lignes cochées est appliquée en dérivation (voir `capabilities`).
    const [rawCapabilities, setRawCapabilities] = useState<FilteringCapabilitiesDto[]>([]);
    const [fieldTypesByKey, setFieldTypesByKey] = useState<Record<string, number>>({});

    // Variables portées par l'ARBRE du filtre, figées au (re)chargement (comme filterValue) : l'édition
    // d'une condition "Variable" ne les recalcule pas. Les variables déclarées dans les grilles de
    // mapping, elles, sont fusionnées en direct (voir `parameters`).
    const [treeParameters, setTreeParameters] = useState<FilterParameterOption[]>([]);

    // Opérations utilisées par le filtre tel qu'il a été chargé, par champ : figées au (re)chargement
    // (comme filterValue), pour que buildFilteringFields garde ces opérations dans la liste d'un champ
    // même si aucun paramètre compatible n'est défini — sinon E1048 à l'affichage de la condition.
    const [initialUsedOperations, setInitialUsedOperations] = useState<Map<string, Set<string>>>(new Map());

    // Dernier Filter connu, lu uniquement lors du (re)chargement des champs (changement de table) :
    // évite que nos propres mises à jour (déclenchées par onValueChanged) ne réinitialisent l'éditeur en cours d'usage.
    const latestFilterRef = useRef<GroupFilterDto | null>(Filter);
    latestFilterRef.current = Filter;

    // Forme canonique du filtre tel qu'il a été chargé / committé. FilterBuilder ré-émet onValueChanged
    // au (re)montage quand on lui (ré)assigne `value` (l'onglet Filters démonte/remonte ce composant
    // quand le splitter d'itération apparaît) : sans cette comparaison, cet écho appellerait SetFilter et
    // marquerait le path comme modifié alors que l'utilisateur n'a rien touché.
    const committedFilterCanonicalRef = useRef<string>('[]');

    useEffect(() => {
        if (TableReferenceId <= 0) {
            setRawCapabilities([]);
            setFieldTypesByKey({});
            setFilterValue([]);
            setInitialUsedOperations(new Map());
            setTreeParameters([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        loadFilteringCapabilities(TableReferenceId).then(({ capabilities: loadedCapabilities, fieldTypesByKey: typesByKey }) => {
            const resolveType = (fieldReferenceId: number) => typesByKey[fieldReferenceId.toString()];
            const derived = deriveFilterExpression(latestFilterRef.current, resolveType);

            setRawCapabilities(loadedCapabilities);
            setFieldTypesByKey(typesByKey);
            setFilterValue(derived);
            committedFilterCanonicalRef.current = canonicalizeExpression(derived, resolveType);
            setInitialUsedOperations(collectOperationsByField(derived));
            setTreeParameters(collectParameterOptions(latestFilterRef.current, resolveType));
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [TableReferenceId]);

    const resolveType = useCallback(
        (fieldReferenceId: number) => fieldTypesByKey[fieldReferenceId.toString()],
        [fieldTypesByKey]
    );

    // Clés stables des grilles de mapping : DeclaredParameters / SelectedFieldReferenceIds sont des
    // tableaux neufs à chaque rendu du parent (dérivés inline), on ne recalcule que sur changement réel.
    const declaredParametersKey = (DeclaredParameters ?? [])
        .map((p) => `${p.TechnicalName}:${p.FieldReferenceId}`)
        .join('|');
    const selectedFieldReferenceIdsKey = (SelectedFieldReferenceIds ?? [])
        .slice()
        .sort((a, b) => a - b)
        .join(',');

    // Picklist des variables : arbre (figé) + variables déclarées dans les grilles de mapping (en direct).
    // Dérivation synchrone -> une variable déclarée devient sélectionnable dès que la grille change, sans
    // dépendre d'un effet ni d'un remontage. `declaredParametersKey` est stable tant que le contenu ne
    // change pas -> aucun churn de `fields` pendant l'édition du filtre.
    const parameters = useMemo(() => {
        const declaredOptions: FilterParameterOption[] = (DeclaredParameters ?? [])
            .filter((declared) => declared.TechnicalName.trim().length > 0)
            .map((declared) => ({
                Id: 0,
                Name: declared.TechnicalName.trim(),
                TechnicalName: declared.TechnicalName.trim(),
                FieldType: resolveType(declared.FieldReferenceId) ?? null,
            }));
        return mergeParameterOptions(treeParameters, declaredOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeParameters, resolveType, declaredParametersKey]);

    // Champs proposés : capacités brutes restreintes aux lignes de mapping cochées (si la liste est
    // fournie), plus les champs déjà référencés par le filtre chargé (initialUsedOperations) pour ne pas
    // casser l'affichage d'une condition existante sur un champ décoché (E1048). Dérivation synchrone ->
    // une colonne cochée apparaît tout de suite.
    // Les champs référencés par les conditions EN COURS d'édition (filterValue) sont aussi conservés :
    // initialUsedOperations est figé au chargement, donc une condition ajoutée ensuite sur un champ dont la
    // ligne de mapping est décochée / retirée ferait disparaître le champ -> E1047 au rendu suivant.
    const currentUsedFieldsKey = useMemo(
        () => Array.from(collectOperationsByField(filterValue).keys()).sort().join(','),
        [filterValue]
    );

    const capabilities = useMemo(() => {
        if (SelectedFieldReferenceIds == null) {
            return rawCapabilities;
        }
        const currentUsedFields = new Set(currentUsedFieldsKey.length > 0 ? currentUsedFieldsKey.split(',') : []);
        return rawCapabilities.filter((capability) => {
            const key = capability.Id.toString();
            return SelectedFieldReferenceIds.some((id) => id.toString() === key) || initialUsedOperations.has(key) || currentUsedFields.has(key);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawCapabilities, selectedFieldReferenceIdsKey, initialUsedOperations, currentUsedFieldsKey]);

    // Un champ ne montre les opérations "Paramètre" que si une variable de même type existe (voir
    // buildFilteringFields dans FilterFieldsShared) ; initialUsedOperations réinjecte les opérations
    // déjà présentes dans le filtre chargé pour éviter E1048.
    // Opérations des conditions EN COURS d'édition, fusionnées à celles du chargement : une condition "=v"
    // ajoutée puis conservée alors que la variable n'est plus déclarée (ou que `fields` est reconstruit)
    // doit rester dans le filterOperations de son champ, sinon E1048. Clé sérialisée -> `fields` n'est
    // reconstruit que si l'ensemble champ/opération change, pas à chaque valeur saisie.
    const currentUsedOperationsKey = useMemo(
        () => JSON.stringify(Array.from(collectOperationsByField(filterValue).entries()).map(([field, ops]) => [field, Array.from(ops).sort()]).sort()),
        [filterValue]
    );
    const usedOperations = useMemo(() => {
        const merged = new Map<string, Set<string>>();
        initialUsedOperations.forEach((ops, field) => merged.set(field, new Set(ops)));
        (JSON.parse(currentUsedOperationsKey) as [string, string[]][]).forEach(([field, ops]) => {
            const set = merged.get(field) ?? new Set<string>();
            ops.forEach((op) => set.add(op));
            merged.set(field, set);
        });
        return merged;
    }, [initialUsedOperations, currentUsedOperationsKey]);

    const fields: Field[] = useMemo(() => buildFilteringFields(capabilities, parameters, usedOperations), [capabilities, parameters, usedOperations]);

    const onValueChanged = useCallback((e: FilterBuilderTypes.ValueChangedEvent) => {
        setFilterValue(e.value);

        // Écho de (re)normalisation émis par FilterBuilder au (re)montage (aucune édition utilisateur) :
        // l'expression est sémantiquement identique au filtre chargé -> ne pas remonter, sinon le path
        // passe en « modifications en cours » à la simple ouverture de l'onglet.
        const canonical = canonicalizeExpression(e.value, resolveType);
        if (canonical === committedFilterCanonicalRef.current) {
            return;
        }

        committedFilterCanonicalRef.current = canonical;
        SetFilter(filterExpressionToGroupFilter(e.value, resolveType));
    }, [SetFilter, resolveType]);

    return (
        <div style={{ position: 'relative', minHeight: 80 }}>
            {/* Monté seulement une fois les champs chargés (et jamais avec fields=[]) : un FilterBuilder créé
                avec une liste vide puis rempli garde un menu « Ajouter une condition » périmé -> createCondition
                sur un champ undefined. La clé le recrée proprement si la table change. */}
            {!loading && fields.length > 0 && (
                <FilterBuilder
                    key={TableReferenceId}
                    value={filterValue} fields={fields} maxGroupLevel={1} groupOperations={["and", "or"]} onValueChanged={onValueChanged}
                    filterOperationDescriptions={CONSTANT_OPERATION_DESCRIPTIONS} >
                    {buildParameterOperations(parameters, fieldTypesByKey).map((operation) => (
                        <FilterBuilderCustomOperation
                            key={operation.name}
                            name={operation.name}
                            caption={operation.caption}
                            icon={operation.icon}
                            editorRender={operation.editorRender}
                            calculateFilterExpression={operation.calculateFilterExpression}
                        />
                    ))}
                </FilterBuilder>
            )}
            <LoadPanel
                position={{ of: '.dx-viewport' }}
                shadingColor="rgba(0,0,0,0.4)"
                visible={loading}
            />
        </div>
    );
}

export default FilterBuilderInline;
